import React, { useContext, useMemo, useReducer, useState } from 'react'
import {
  useApiListGroup,
  useApiListNodeDecendantContent,
  useApiListNodeDecendantLibrary,
  useApiListNodeDecendantTemplate,
  useApiListRoleOrg
} from '../../../../../../api-new/state/content'
import { useLoad } from '../../../../../../shared/hooks/load'
import { useRoleOrgGroup, useRoleOrgNode } from '../../../../../../role/hook/role-org'

const Context = React.createContext()

export const useAddUserContext = () => useContext(Context)

export const autoUserRoles = roles => (roles || []).filter(role => role && role.isAutoUser)

export const roleLabel = role => (role && (role.name || role.role)) || ''

// Role ids for an invite payload: auto roles first, then the rest, no repeats.
export const inviteRoleIDs = (...lists) => [...new Set(lists.flat().filter(Boolean).map(role => role.id))]


export const AddUserContext = ({ children }) => {
  const roleOrgGroup = useRoleOrgGroup()
  const roleOrgNode = useRoleOrgNode()

  const apiListRoleOrg = useApiListRoleOrg()
  const apiListGroup = useApiListGroup()
  const apiListNodeDecendantContent = useApiListNodeDecendantContent()
  const apiListNodeDecendantTemplate = useApiListNodeDecendantTemplate()
  const apiListNodeDecendantLibrary = useApiListNodeDecendantLibrary()

  useLoad(() => apiListRoleOrg.get(), [])
  useLoad(() => roleOrgGroup.accessRead && apiListGroup.get(), [])
  useLoad(() => roleOrgNode.accessRead && apiListNodeDecendantContent.get(), [])
  useLoad(() => roleOrgNode.accessRead && apiListNodeDecendantTemplate.get(), [])
  useLoad(() => roleOrgNode.accessRead && apiListNodeDecendantLibrary.get(), [])

  const [commonRoles, updateCommonRoles] = useState([])
  const [commonGroups, updateCommonGroups] = useState([])
  const [commonContents, updateCommonContents] = useState([])
  const [commonTemplates, updateCommonTemplates] = useState([])
  const [commonLibraries, updateCommonLibraries] = useState([])

  const allRoles = useMemo(() => apiListRoleOrg.items, [apiListRoleOrg.items])
  // Every member always gets the auto roles (role_orgs.is_auto_user). They
  // are read from the roles table, never matched by name, and the server
  // applies them on invite and on every acceptance path regardless.
  const autoRoles = useMemo(() => autoUserRoles(allRoles), [allRoles])
  const allGroups = useMemo(() => apiListGroup.items, [apiListGroup.items])
  const allContents = useMemo(() => apiListNodeDecendantContent.items, [apiListNodeDecendantContent.items])
  const allTemplates = useMemo(() => apiListNodeDecendantTemplate.items, [apiListNodeDecendantTemplate.items])
  const allLibraries = useMemo(() => apiListNodeDecendantLibrary.items, [apiListNodeDecendantLibrary.items])

  const [userErrors, setUserError, clearUserError] = useUserErrorData()

  return (
    <Context.Provider
      value={{
        allRoles,
        autoRoles,
        allGroups,
        allContents,
        allTemplates,
        allLibraries,
        userErrors,
        setUserError,
        clearUserError,
        commonRoles,
        updateCommonRoles,
        commonGroups,
        updateCommonGroups,
        commonContents,
        updateCommonContents,
        commonTemplates,
        updateCommonTemplates,
        commonLibraries,
        updateCommonLibraries
      }}>
      {children}
    </Context.Provider>
  )
}

const ERROR_TYPES = {
  SET_ERROR: 1,
  CLEAR_ERROR: 2
}

const useUserErrorData = () => {
  const [userErrors, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case ERROR_TYPES.SET_ERROR:
        let setError = { ...state }
        let userError = setError[action.index]
        if (!userError) {
          userError = {}
        }
        userError[action.name] = action.value
        setError[action.index] = userError
        return setError
      case ERROR_TYPES.CLEAR_ERROR:
        let clearError = { ...state }
        delete clearError[action.index]
        return clearError
      default:
        return state
    }
  }, {})

  const setUserError = (index, name, value) => dispatch({ type: ERROR_TYPES.SET_ERROR, index: index, name: name, value: value })
  const clearUserError = index => dispatch({ type: ERROR_TYPES.CLEAR_ERROR, index: index })

  return [userErrors, setUserError, clearUserError]
}
